import {
    DocumentType,
    modelOptions,
    mongoose,
    PropType,
    plugin,
    post,
    pre,
    prop,
    type Ref,
    Severity,
} from "@typegoose/typegoose";
import mongooseAutoPopulate from "mongoose-autopopulate";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import { DeviceTypeConfigurations } from "../../Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { RootLogger } from "../../logging/index.js";
import { type FirstPartyDeviceType } from "../../shared.js";
import { PinoModules } from "../../utils/index.js";
import { generateShortCodeFromEui, toLoraEui } from "../../utils/shortCodes/EuiShortCodeMapper.js";
import {
    ENTITY_NAME_MAX_LENGTH,
    entityNameZodValidator,
    NodeIdString,
    ObjectIdString,
    VerdiIdString,
} from "../../validators/index.js";
import { DeviceTypeString, getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { assetIdentityByLoraEui } from "../assetIdentity/assetIdentityByLoraEui.js";
import { assetIdentitySync } from "../assetIdentity/assetIdentitySyncHook.js";
import { DeviceVersion } from "../commonSubSchemas/deviceVersion.js";
import { ExternalLink } from "../commonSubSchemas/externalLink.js";
import { ValveState } from "../commonSubSchemas/valveState.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { type Organization } from "../organization/organization.js";
import { ResponseTimeStats } from "../responseTimeStats/responseTimeStats.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { Site } from "../site/site.js";
import type { DeviceFieldsWithDefaults, DeviceSchemaTypes } from "../types/device.d.js";
import { createDelayedRegistrationHook } from "../utils/delayedRegistrationHook.js";
import { addPositionSyncSaveHook, addPositionSyncUpdateHook, PositionDepthSource } from "../utils/pointPositionSync.js";
import { siteIdSyncSaveHook } from "../utils/siteIdSync.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { Warning } from "../warning/warning.js";
import { Zone } from "../zone/zone.js";
import * as getters from "./getters.js";
// TEMPORARY (PR #1284) — remove with the @pre decorator below once the $unset migration has run.
import { excludeLegacyDeviceFields } from "./legacyFieldProjection.js";
import { Gen4NetworkInfo } from "./subSchemas/gen4NetworkInfo.js";
import { LoraNetworkProfileConfig } from "./subSchemas/loraNetworkProfileConfig.js";
import { SensorSpecifier } from "./subSchemas/sensorSpecifier.js";
import { SproutAutomationSettings } from "./subSchemas/sproutAutomationSettings.js";
import { SproutNetworkInfo } from "./subSchemas/sproutNetworkInfo.js";
import { SproutVerificationInfo } from "./subSchemas/sproutVerification/sproutVerificationInfo.js";
import { VerificationCheckConfiguration } from "./subSchemas/verificationCheckConfiguration.js";
import * as utilities from "./utilities.js";

const logger = RootLogger.child({ module: PinoModules.models.assetIdentity });

const SCHEMA_KEY = "Device" as const;
/** MongoDB collection name for {@link Device} documents. */
export const DEVICE_COLLECTION_NAME = "devices" as const;

export const CONNECTED_EQUIPMENT_TYPES = ["valve", "pump"] as const;
export type ConnectedEquipment = (typeof CONNECTED_EQUIPMENT_TYPES)[number];
const connectedEquipmentZod = z.enum(CONNECTED_EQUIPMENT_TYPES);

export const generatedSensorClusterSyncPreSave = createDelayedRegistrationHook<
    (document: DocumentType<Device>) => void
>("generatedSensorClusterSyncPreSave", { synchronous: true });

export const generatedSensorClusterSyncPostSave = createDelayedRegistrationHook<
    (document: DocumentType<Device>) => Promise<void>
>("generatedSensorClusterSyncPostSave");

// TEMPORARY (PR #1284) — remove with the legacyFieldProjection import once the $unset migration has run.
@pre<Device>(["find", "findOne", "findOneAndUpdate"], excludeLegacyDeviceFields)
@pre<Device>(...addPositionSyncSaveHook(PositionDepthSource.POSITION_DEPTH))
@pre<Device>(...addPositionSyncUpdateHook(PositionDepthSource.POSITION_DEPTH))
@pre<Device>(...siteIdSyncSaveHook)
// Generates verdiId on "validate" rather than "save" since verdiId is a required field
@pre<Device>("validate", async function (this: DocumentType<Device>) {
    // we want to move to the assetIdentity being the source of truth for this stuff, for now, this will help get
    // everything synced up. Should remove this eventually.
    let identity;
    if (this.node_id) {
        const loraEui = toLoraEui(this.node_id);
        try {
            identity = await assetIdentityByLoraEui.get(loraEui);
        } catch (e) {
            logger.warn({ error: e, loraEui }, "could not read asset identity by lora eui");
        }
    }
    if (identity?.verdiId) {
        this.verdiId = identity.verdiId;
    } else if (this.node_id) {
        try {
            const shortCode = generateShortCodeFromEui(this.node_id);
            if (shortCode === "???-???") {
                logger.warn({ node_id: this.node_id }, "no short-code range for device node_id; skipping verdiId");
            } else {
                this.verdiId = `D-${shortCode}`;
            }
        } catch (e) {
            logger.warn({ error: e, node_id: this.node_id }, "could not generate verdiId for device");
        }
    }
    if (!this.verdiId) {
        // Surfaced instead of the generic "verdiId is required" validation error to help diagnose
        throw new Error(`Could not derive a verdiId for device with node_id "${this.node_id}"`);
    }
})
@pre<Device>("save", async function (this: DocumentType<Device>) {
    await generatedSensorClusterSyncPreSave.function(this);
})
@post<Device>("save", async function (this: DocumentType<Device>) {
    await generatedSensorClusterSyncPostSave.function(this);
})
@post<Device>("save", async function (this: DocumentType<Device>) {
    if (!this.verdiId) return;
    // TODO: Once AssetIdentity becomes the source of truth for asset IDs, this should hard-fail instead of
    // catching. We swallow errors here only because the transition is incomplete — device saves must not
    // break if the sync fails during this interim period.
    await assetIdentitySync
        .function({
            loraEui: this.node_id ? toLoraEui(this.node_id) : undefined,
            assetCollection: DEVICE_COLLECTION_NAME,
            // DeviceType is a plain string, so a legacy or retired value can miss the table.
            assetType: DeviceTypeConfigurations[this.DeviceType as FirstPartyDeviceType]?.assetType ?? AssetType.DEVICE,
            verdiId: this.verdiId,
        })
        .catch((e) => logger.warn({ error: e, verdiId: this.verdiId }, "could not sync asset identity for device"));
})
@pre<Device>("save", async function saveDevice(this: DocumentType<Device>) {
    try {
        // ensures sensor indexes remain always correctly assigned
        if (this.connectedSensors) {
            if (this.connectedSensors[0] && this.connectedSensors[0].sensorIndex !== 0) {
                this.connectedSensors[0].sensorIndex = 0;
            }
            if (this.connectedSensors[1] && this.connectedSensors[1].sensorIndex !== 1) {
                this.connectedSensors[1].sensorIndex = 1;
            }
        }
    } catch (e) {
        console.warn(e, `Error in adding connected zones to connected sensors ${this.node_id}`);
    }

    // Mark externalLinks as modified if changes detected
    if (this.isModified("externalLinks")) {
        this.markModified("externalLinks");
    }

    if (this.adminNotes === undefined) {
        this.adminNotes = "";
    }
    if (this.notes === undefined) {
        this.notes = "";
    }
})
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
        collection: DEVICE_COLLECTION_NAME,
        timestamps: true,
    },
})
@plugin(mongooseAutoPopulate as any)
@post<SchemaBase<Device>>(...addSchemaKeyHook(SCHEMA_KEY))
export class Device extends SchemaBase<Device, DeviceFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    // EntityWithOwnerBase implementation
    @validate({ zodValidator: entityNameZodValidator })
    @prop({ type: () => String, required: true, maxlength: ENTITY_NAME_MAX_LENGTH })
    public name!: string;

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: () => "" })
    public notes: string = "";

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: () => "" })
    public adminNotes: string = "";

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    // Device-specific fields
    @validate({ zodValidator: NodeIdString })
    @prop({ type: () => String, required: true, unique: true })
    public node_id!: string;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner?: Ref<Organization>;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public loraApplicationId?: string;

    @validate({ zodValidator: z.string(), responseOnly: true })
    @prop({ type: () => String, required: true, unique: true })
    public verdiId!: string;

    // TODO: Update to actually reflect being Zone References
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ type: () => [mongoose.Types.ObjectId], required: true, ref: "Zone" }, PropType.ARRAY)
    public connectedZones?: Ref<Zone>[];

    @validate({
        childClass: SensorSpecifier,
        // Nullable to tolerate legacy documents where null entries are stored in the array
        childClassTransform: (object) => z.array(object.nullable()).optional(),
    })
    @prop({ type: () => [SensorSpecifier], required: true, _id: false }, PropType.ARRAY)
    public connectedSensors?: SensorSpecifier[];

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public latitude?: number;

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public longitude?: number;

    @validate({ childClass: VertexInfo, childClassTransform: (object) => object.optional(), responseOnly: true })
    @prop({ type: () => VertexInfo, required: false, _id: false })
    public position?: VertexInfo;

    @validate({ zodValidator: VerdiIdString.optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public siteId?: string;

    @validate({ zodValidator: ObjectIdString.optional(), responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "AreaOfInterest" })
    public _aoi?: Ref<Site>;

    @validate({
        childClass: ExternalLink,
        childClassTransform: (object) => z.record(z.string(), object).optional(),
    })
    @prop({ type: () => Object, required: true, default: {}, allowMixed: Severity.ALLOW })
    public externalLinks: Record<string, ExternalLink> = {};

    @validate({ childClass: Warning, childClassTransform: (object) => z.array(object).optional(), responseOnly: true })
    @prop({
        ref: () => Warning,
        localField: "node_id",
        foreignField: "foreignLookup.targetId",
        match: { "foreignLookup.targetCollection": DEVICE_COLLECTION_NAME, versionIsCurrent: true, deleted: false },
        autopopulate: true,
    })
    public warnings: Warning[] = [];

    @validate({ zodValidator: z.any().optional(), responseOnly: true })
    @prop({
        ref: () => ResponseTimeStats,
        localField: "node_id",
        foreignField: "foreignLookup.targetId",
        match: { "foreignLookup.targetCollection": DEVICE_COLLECTION_NAME },
        justOne: true,
        virtual: true,
        autopopulate: true,
    })
    public responseTimeStats: ResponseTimeStats | undefined = undefined;

    @validate({
        childClass: DeviceVersion,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => DeviceVersion, required: false })
    public version?: DeviceVersion;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastContact?: Date;

    @validate({ zodValidator: connectedEquipmentZod.optional() })
    @prop({ type: () => String, required: false, default: () => "valve" })
    public connectedEquipment: ConnectedEquipment = "valve";

    @validate({
        childClass: ValveState,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => ValveState, required: false, _id: false })
    public valveState?: ValveState;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastValveStateChange?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastPositionChange?: Date;

    @validate({ zodValidator: DeviceTypeString })
    @prop({ type: () => String, required: true })
    public DeviceType: string;

    // Request input only — no @prop, so it is never stored or returned. Registration still accepts it
    // as an alias for the EUI, and the create/update validators are strictObjects that would 400 on it
    // otherwise. node_id carries the value.
    @validate({ zodValidator: z.string().optional() })
    public EnclosureID?: string;

    @validate({ zodValidator: z.any().optional() })
    @prop({ type: () => SproutAutomationSettings, required: false, _id: false })
    public sproutAutomationSettings?: SproutAutomationSettings;

    @validate({ zodValidator: z.any().optional() })
    @prop({ type: () => SproutVerificationInfo, required: false, _id: false })
    public sproutIrrigationVerification?: SproutVerificationInfo;

    @validate({
        childClass: SproutNetworkInfo,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => SproutNetworkInfo, required: false, _id: false })
    public sproutNetworkInfo?: SproutNetworkInfo;

    @validate({ childClass: Gen4NetworkInfo, childClassTransform: (object) => object.optional(), responseOnly: true })
    @prop({ type: () => Gen4NetworkInfo, required: false, _id: false })
    public gen4NetworkInfo?: Gen4NetworkInfo;

    @validate({ zodValidator: z.array(z.any()).optional() })
    @prop({ type: () => [VerificationCheckConfiguration], required: false, default: () => [] }, PropType.ARRAY)
    public verificationCheckConfigurations: VerificationCheckConfiguration[] = [];

    @validate({ zodValidator: z.any().optional() })
    @prop({
        type: () => LoraNetworkProfileConfig,
        _id: false,
        default: new LoraNetworkProfileConfig(),
        required: true,
        autopopulate: true,
    })
    public loraNetworkProfileConfig: LoraNetworkProfileConfig = new LoraNetworkProfileConfig();

    static getters = { ...getters } as const;

    static utilities = { ...utilities } as const;

    static dtoValidators: DtoValidatorTypes<DeviceSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<Device, DeviceFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

Device.dtoValidators = getZodSchemaFromClass(Device) as unknown as DtoValidatorTypes<DeviceSchemaTypes>;
